import React, { useEffect, useMemo, useState } from "react";
import apiFetch from "./utils/api";
import "./ReportsPage.css";

/*
  ReportsPage
  - fetches aggregated timesheet report data for a year
  - fetches admin users so we can compute expected monthly hours per user
  - displays a table of Resources × Months with highlight rules for under-expected months

  Behavior and highlights are preserved from the original implementation.
*/

const DEFAULT_SHIFT_FLAGS = [false, true, true, true, true, true, false];

const normalizeDaily = (u) => {
  if (!u) return undefined;
  if (typeof u.UserDailyHours !== "undefined" && u.UserDailyHours !== null)
    return Number(u.UserDailyHours);
  return undefined;
};

const normalizeFlags = (u) => {
  if (u === undefined) return DEFAULT_SHIFT_FLAGS;
  
  const raw = [
    (u.UserShiftMask & 1) > 0,
    (u.UserShiftMask & 2) > 0,
    (u.UserShiftMask & 4) > 0,
    (u.UserShiftMask & 8) > 0,
    (u.UserShiftMask & 16) > 0,
    (u.UserShiftMask & 32) > 0,
    (u.UserShiftMask & 64) > 0,
  ];
  const flags = raw.map((v) => {
    if (v === null || typeof v === "undefined") return false;
    if (typeof v === "boolean") return v;
    const s = String(v).toLowerCase();
    return s === "1" || s === "true" || s === "y" || s === "yes" || s === "on";
  });
  // fallback Mon-Fri if nothing explicitly set
  if (flags.every((f) => !f)) return DEFAULT_SHIFT_FLAGS;
  return flags;
};

const countShiftDaysInRange = (start, end, shiftFlags) => {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  if (e < s) return 0;
  let count = 0;
  const cur = new Date(s);
  while (cur <= e) {
    const wd = cur.getDay(); // 0..6 Sun..Sat
    if (shiftFlags && shiftFlags[wd]) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const ReportsPage = ({ onClose } = {}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [usersByCode, setUsersByCode] = useState({});

  // Fetch report data for a year
  const fetchReport = async (y) => {
    setLoading(true);
    setError("");
    try {
      const body = await apiFetch(`/api/reports?year=${y}`);
      setMonths(body.months || []);
      setRows(body.data || []);
    } catch (e) {
      setError(e.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // Fetch admin users once (used to compute expected per-user arrays)
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/users");
        const list = Array.isArray(res)
          ? res.users || res
          : res && Array.isArray(res.users)
          ? res.users
          : [];
        const map = {};
        list.forEach((u) => {
          const raw = (u.UserCode || u.user || "").toString();
          const code = raw;
          const codeLower = raw.trim().toLowerCase();
          if (code) map[code] = u;
          if (codeLower && !map[codeLower]) map[codeLower] = u;
        });
        setUsersByCode(map);
      } catch (e) {
        // ignore fetch errors for users — reports will still render with fallback expectations
      }
    })();
  }, []);

  // Build expected hours arrays per user from usersByCode and months/year
  const expectedByUser = useMemo(() => {
    const map = {};
    const monthsCount = months.length || 12;
    const seen = new Set();

    Object.values(usersByCode).forEach((u) => {
      const rawCode = (u && (u.UserCode || u.user || "")).toString();
      const codeKey = rawCode || "";
      if (!codeKey || seen.has(codeKey)) return;
      seen.add(codeKey);

      const daily = normalizeDaily(u);
      const flags = normalizeFlags(u);

      const arr = Array(monthsCount).fill(0);
      for (let mi = 0; mi < monthsCount; mi++) {
        const monthStart = new Date(year, mi, 1);
        const monthEnd = new Date(year, mi + 1, 0);
        const workdays = countShiftDaysInRange(monthStart, monthEnd, flags);
        // when user exists but daily is undefined, treat expected as 0 (explicit zero)
        arr[mi] = typeof daily === "undefined" ? 0 : workdays * Number(daily);
      }

      // register several key variants for quick lookup
      map[codeKey] = arr;
      map[codeKey.trim().toLowerCase()] = arr;
      const short = codeKey.includes("\\")
        ? codeKey.split("\\").pop()
        : codeKey;
      map[short] = arr;
      map[short.trim().toLowerCase()] = arr;
    });

    return map;
  }, [usersByCode, months, year]);

  // Helper: try to find user object for a given resource string using a few heuristics
  const findUserForResource = (resource) => {
    if (!resource) return null;
    const rraw = String(resource);
    const cand = [
      rraw,
      rraw.trim(),
      rraw.toLowerCase(),
      rraw.trim().toLowerCase(),
    ];
    if (rraw.includes("\\")) {
      const part = rraw.split("\\").pop();
      cand.push(part, part.trim(), part.toLowerCase());
    }
    if (rraw.includes("@")) {
      const local = rraw.split("@")[0];
      cand.push(local, local.trim(), local.toLowerCase());
    }
    for (const c of cand) {
      if (usersByCode && usersByCode[c]) return usersByCode[c];
    }
    const lower = rraw.toLowerCase().trim();
    for (const k of Object.keys(usersByCode || {})) {
      if (!k) continue;
      const kl = String(k).toLowerCase();
      if (kl === lower || kl.endsWith(lower) || kl.includes(lower))
        return usersByCode[k];
    }
    return null;
  };

  // Compute expected hours (per-user) from a user object for a specific month index
  const computeUserExpectedForMonth = (userObj, monthIndex) => {
    const udaily = normalizeDaily(userObj);
    if (typeof udaily === "undefined") return undefined;
    const ms = new Date(year, monthIndex, 1);
    const me = new Date(year, monthIndex + 1, 0);
    const flags = normalizeFlags(userObj);
    const w = countShiftDaysInRange(ms, me, flags);
    return w * Number(udaily);
  };

  // Render
  return (
    <div style={{ padding: 20 }}>
      <h2>Summary Report</h2>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label style={{ fontWeight: 600 }}>Year</label>
        <button onClick={() => setYear((y) => y - 1)}>-</button>
        <input
          type="number"
          value={year}
          onChange={(e) =>
            setYear(Number(e.target.value || new Date().getFullYear()))
          }
          style={{ width: 100 }}
        />
        <button onClick={() => setYear((y) => y + 1)}>+</button>
        <div style={{ marginLeft: 12 }}>
          <button onClick={() => fetchReport(year)}>Refresh</button>
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <div style={{ color: "darkred" }}>{error}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <div className="reports-legend" style={{ marginBottom: 8 }}>
            <div className="legend-item">
              <div className="legend-swatch legend-strong" />
              <div>Under-Expected (Shift)</div>
            </div>
            <div className="legend-item">
              <div className="legend-swatch legend-warning" />
              <div>Under-Expected (7.5)</div>
            </div>
            <div className="legend-item">
              <div className="legend-swatch legend-mild" />
              <div>No Shift</div>
            </div>
          </div>

          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "left",
                  }}
                >
                  Resource
                </th>
                {months.map((m) => (
                  <th key={m} style={{ border: "1px solid #ddd", padding: 8 }}>
                    {m}
                  </th>
                ))}
                <th style={{ border: "1px solid #ddd", padding: 8 }}>Total</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => {
                const rowTotal = (r.months || []).reduce(
                  (a, b) => a + Number(b || 0),
                  0
                );
                return (
                  <tr key={r.resource}>
                    <td style={{ border: "1px solid #eee", padding: 8 }}>
                      {r.resource}
                    </td>

                    {(r.months || []).map((val, i) => {
                      const actual = Number(val || 0);

                      // Resolve expected arrays using precomputed expectedByUser or a sensible default
                      const resolveExpectedArr = (resource) => {
                        if (!resource) return [];
                        const cand = [];
                        const rraw = String(resource);
                        cand.push(
                          rraw,
                          rraw.trim(),
                          rraw.toLowerCase(),
                          rraw.trim().toLowerCase()
                        );
                        if (rraw.includes("\\")) {
                          const part = rraw.split("\\").pop();
                          cand.push(part, part.trim(), part.toLowerCase());
                        }
                        if (rraw.includes("@")) {
                          const local = rraw.split("@")[0];
                          cand.push(local, local.trim(), local.toLowerCase());
                        }
                        for (const c of cand)
                          if (expectedByUser[c]) return expectedByUser[c];
                        const lower = rraw.toLowerCase().trim();
                        for (const k of Object.keys(expectedByUser || {})) {
                          if (!k) continue;
                          const kl = String(k).toLowerCase();
                          if (
                            kl === lower ||
                            kl.endsWith(lower) ||
                            kl.includes(lower)
                          )
                            return expectedByUser[k];
                        }
                        return [];
                      };

                      // Default expected (unmatched resources) => Mon-Fri × 7.5h
                      const defaultExpectedArr = (() => {
                        const mcount = months.length || 12;
                        const def = Array(mcount).fill(0);
                        const flagsDefault = [
                          false,
                          true,
                          true,
                          true,
                          true,
                          true,
                          false,
                        ];
                        for (let mi2 = 0; mi2 < mcount; mi2++) {
                          const ms = new Date(year, mi2, 1);
                          const me = new Date(year, mi2 + 1, 0);
                          const w = countShiftDaysInRange(ms, me, flagsDefault);
                          def[mi2] = w * 7.5;
                        }
                        return def;
                      })();

                      const expectedArr = resolveExpectedArr(r.resource);
                      const matched = expectedArr && expectedArr.length > 0;
                      const finalExpectedArr = matched
                        ? expectedArr
                        : defaultExpectedArr;
                      const exp = finalExpectedArr[i] || 0;

                      // Only highlight past months
                      const monthEnd = new Date(year, i + 1, 0);
                      const now = new Date();
                      const isPast = monthEnd < now;

                      // Determine highlight class according to rules
                      let cellClass = "";
                      if (isPast) {
                        // if (matched) {
                        //   if (exp === 0 && actual === 0)
                        //     cellClass = " report-cell--mild";
                        //   else if (exp > 0 && actual < exp)
                        //     cellClass = " report-cell--strong";
                        // } else {
                        //   if (exp > 0 && actual < exp)
                        //     cellClass = " report-cell--warning";
                        // }
                        if (exp > 0 && (actual / exp) < 0.9) cellClass = " report-cell--warning"
                        if (exp > 0 && (actual / exp) < 0.7) cellClass = " report-cell--strong"
                        
                      }

                      // Prefer per-user calculated expected (if user exists and has daily defined)
                      let shownExpected = exp;
                      const matchedUser = findUserForResource(r.resource);
                      if (matchedUser) {
                        const ud = computeUserExpectedForMonth(matchedUser, i);
                        if (typeof ud !== "undefined") shownExpected = ud;
                      }

                      const title = `Expected ${shownExpected.toFixed(
                        2
                      )}h — Actual ${actual.toFixed(2)}h`;
                      return (
                        <td
                          key={i}
                          className={`report-cell${cellClass}`}
                          style={{ border: "1px solid #eee", padding: 8 }}
                          title={title}
                        >
                          {actual.toFixed(2)}
                        </td>
                      );
                    })}

                    <td
                      style={{
                        border: "1px solid #eee",
                        padding: 8,
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      {rowTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <button onClick={() => (onClose ? onClose() : null)}>Back</button>
      </div>
    </div>
  );
};

export default ReportsPage;
